import React from 'react';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

const Logo: React.FC<LogoProps> = ({ 
  size = 'md', 
  showText = true, 
  className = '' 
}) => {
  const sizeClasses = {
    sm: {
      container: 'h-8',
      icon: 'h-8 w-8',
      text: 'text-lg'
    },
    md: {
      container: 'h-10',
      icon: 'h-10 w-10',
      text: 'text-xl'
    },
    lg: {
      container: 'h-12',
      icon: 'h-12 w-12',
      text: 'text-2xl'
    }
  };

  const IconComponent = () => (
    <svg
      className={`${sizeClasses[size].icon} text-primary-600`}
      fill="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Money Machine Icon - Custom SVG combining currency and gear elements */}
      <defs>
        <linearGradient id="moneyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      
      {/* Main circular background */}
      <circle 
        cx="12" 
        cy="12" 
        r="11" 
        fill="url(#moneyGradient)" 
        opacity="0.1"
      />
      
      {/* Gear teeth (machine element) */}
      <g fill="url(#moneyGradient)">
        <path d="M12 2l1.5 3h-3L12 2z" />
        <path d="M22 12l-3 1.5v-3L22 12z" />
        <path d="M12 22l-1.5-3h3L12 22z" />
        <path d="M2 12l3-1.5v3L2 12z" />
        <path d="M19.07 4.93l-2.12 2.12-1.06-1.06L19.07 4.93z" />
        <path d="M4.93 19.07l2.12-2.12 1.06 1.06L4.93 19.07z" />
        <path d="M19.07 19.07l-2.12-2.12 1.06-1.06L19.07 19.07z" />
        <path d="M4.93 4.93l2.12 2.12-1.06 1.06L4.93 4.93z" />
      </g>
      
      {/* Inner circle with dollar sign */}
      <circle 
        cx="12" 
        cy="12" 
        r="6" 
        fill="none" 
        stroke="url(#moneyGradient)" 
        strokeWidth="1.5"
      />
      
      {/* Dollar sign */}
      <g fill="url(#moneyGradient)" fontSize="10" fontWeight="600">
        <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="700">$</text>
      </g>
      
      {/* Small cogs/gears around */}
      <g fill="url(#moneyGradient)" opacity="0.6">
        <circle cx="6" cy="6" r="1.5" />
        <circle cx="18" cy="6" r="1.5" />
        <circle cx="6" cy="18" r="1.5" />
        <circle cx="18" cy="18" r="1.5" />
      </g>
    </svg>
  );

  return (
    <div className={`flex items-center ${sizeClasses[size].container} ${className}`}>
      <IconComponent />
      {showText && (
        <div className="ml-3">
          <span className={`font-bold text-primary-900 dark:text-primary-100 ${sizeClasses[size].text}`}>
            Money
          </span>
          <span className={`font-bold text-primary-600 ${sizeClasses[size].text} ml-1`}>
            Machine
          </span>
        </div>
      )}
    </div>
  );
};

export default Logo; 